import { Directive, EventEmitter, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Address } from '../../../common/model/address';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { Fragment } from '../../../core/model/fragment';
import { LocalDate } from '../../../core/model/local-date';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Company } from '../../model/company';
import { LeaseRow } from '../../model/lease-row';
import { Property } from '../../model/property';
import { RentalObject } from '../../model/rental-object';
import { RentalObjectType } from '../../model/rental-object-type';
import { CompanyService } from '../../service/data/company.service';
import { PropertyService } from '../../service/data/property.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';
import { LeaseRowsComponent } from '../lease-rows/lease-rows.component';
import { DataService } from './../../../core/service/base/data.service';
import { RentalObjectImprovement } from '../../model/rental-object-improvement';
import { ActivatedRoute } from '@angular/router';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FaultNotification } from '../../../fault-notification/model/faultNotification';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { LeaseAgreementTemplate } from '../../model/lease-agreement-template';
import {
  ColdWaterPaymentKind,
  ColdWaterPaymentKindToString,
  HeatingPaymentKind,
  HeatingPaymentKindToString,
  HotWaterPaymentKind,
  HotWaterPaymentKindToString,
  HouseholdElectricityPaymentKind,
  HouseholdElectricityPaymentKindToString,
  ParkingElectricityPaymentKind,
  ParkingElectricityPaymentKindToString,
  InvoicePeriod,
  InvoicePeriodToString,
} from '../../model/const/enums';
import { RentalObjectLogService } from '../../service/data/rental-object-log.service';
import { LogRecord } from '../../../common/model/log-record';
import { RentalObjectKey } from '../../model/rental-object-key';
import { BrfInnerFundTransaction } from '../../model/brf-inner-fund-transaction';
import { BrfMortgagePrescription } from '../../model/brf-mortgage-prescription';
import { LicenseService } from '../../../common/service/data/license.service';
import { LicenceLimitComparison } from '../../../common/service/data/license.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { RentalObjectDocumentManagerComponent } from '../rental-object-document-manager/rental-object-document-manager.component';
import { CommandBase } from '../../../core/model/command';
import { RentalObjectRentalBlock } from '../../model/rental-object-rental-block';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels, defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { RentalObjectCategory } from '../../model/const/enums';

import { take } from 'rxjs/operators';
import { Article } from '../../model/article';
import { SelectionItem } from '../../../core/component/select/select.component';
import { toPropertyTaxPercentageDisplayValue } from '../../helper/property-tax.helpers';

const rentalObjectFormModelNames = {
  company: 'company',
  property: 'property',
  rentalId: 'rentalId',
  surface: 'surface',
  rentalObjectType: 'rentalObjectType',
};

@Directive()
export abstract class RentalObjectFormComponent<TModel extends RentalObject> extends FormComponentBase<TModel> {
  public readonly documentsFragment = Fragment.documentsTab;
  protected abstract get category(): RentalObjectCategory;

  constructor(
    fb: FormBuilder,
    protected fc: FormControlExFactory,
    private companyService: CompanyService,
    private propertyService: PropertyService,
    protected rentalObjectTypeService: RentalObjectTypeService,
    protected faultNotificationService: FaultNotificationService,
    private _columnDefinitionService: ColumnDefinitionService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver,
    service: DataService<TModel>,
    messageService: MessageService,
    navigation: NavigationService,
    columnDefinitionService: ColumnDefinitionService,
    private logService: RentalObjectLogService,
    private licenseService: LicenseService,
    private readonly dimensionLabelsService: DimensionLabelsService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this._fb = fb;
    this.addDeleteMenu = false;
    this.leaseRowStart = this._getLeaseRowStart();
    this.faultNotificationsColumnDefinition = columnDefinitionService.getRentalObjectFaultNotificationsColumnDefinition();
    this.logColumnDefinition = this._columnDefinitionService.create('Log');
  }

  @ViewChild(LeaseRowsComponent) leaseRowComponent: LeaseRowsComponent;
  @Output() submitEvent = new EventEmitter<void>();
  @ViewChild(RentalObjectDocumentManagerComponent) rentalObjectDocumentManager: RentalObjectDocumentManagerComponent;

  faultNotificationsColumnDefinition: ColumnDefinition[];

  heatingPayments = [
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IncludedInTheRent), value: HeatingPaymentKind.IncludedInTheRent },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IndividualMeasurement), value: HeatingPaymentKind.IndividualMeasurement },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.CostIsAdded), value: HeatingPaymentKind.CostIsAdded },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.SeparateSubscription), value: HeatingPaymentKind.SeparateSubscription },
  ];
  coldWaterPayments = [
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IncludedInTheRent), value: ColdWaterPaymentKind.IncludedInTheRent },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IndividualMeasurement), value: ColdWaterPaymentKind.IndividualMeasurement },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.AccordingToTemplate), value: ColdWaterPaymentKind.AccordingToTemplate },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.CostIsAdded), value: ColdWaterPaymentKind.CostIsAdded },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.SeparateSubscription), value: ColdWaterPaymentKind.SeparateSubscription },
  ];
  hotWaterPayments = [
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.IncludedInTheRent), value: HotWaterPaymentKind.IncludedInTheRent },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.IndividualMeasurement), value: HotWaterPaymentKind.IndividualMeasurement },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.AccordingToTemplate), value: HotWaterPaymentKind.AccordingToTemplate },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.CostIsAdded), value: HotWaterPaymentKind.CostIsAdded },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.SeparateSubscription), value: HotWaterPaymentKind.SeparateSubscription },
  ];
  householdElectricityPayments = [
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IncludedInTheRent),
      value: HouseholdElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IndividualMeasurement),
      value: HouseholdElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.SeparateSubscription),
      value: HouseholdElectricityPaymentKind.SeparateSubscription,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.AccordingToTemplate),
      value: HouseholdElectricityPaymentKind.AccordingToTemplate,
    },
  ];
  parkingElectricityPayments = [
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IncludedInTheRent),
      value: ParkingElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IndividualMeasurement),
      value: ParkingElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption),
      value: ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.SpecifiedInSpecificTerms),
      value: ParkingElectricityPaymentKind.SpecifiedInSpecificTerms,
    },
  ];

  heatingPaymentsBrf = [
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IncludedInTheRent, true), value: HeatingPaymentKind.IncludedInTheRent },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IndividualMeasurement, true), value: HeatingPaymentKind.IndividualMeasurement },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.CostIsAdded, true), value: HeatingPaymentKind.CostIsAdded },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.SeparateSubscription, true), value: HeatingPaymentKind.SeparateSubscription },
  ];
  coldWaterPaymentsBrf = [
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IncludedInTheRent, true), value: ColdWaterPaymentKind.IncludedInTheRent },
    {
      name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IndividualMeasurement, true),
      value: ColdWaterPaymentKind.IndividualMeasurement,
    },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.AccordingToTemplate, true), value: ColdWaterPaymentKind.AccordingToTemplate },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.CostIsAdded, true), value: ColdWaterPaymentKind.CostIsAdded },
    {
      name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.SeparateSubscription, true),
      value: ColdWaterPaymentKind.SeparateSubscription,
    },
  ];
  hotWaterPaymentsBrf = [
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.IncludedInTheRent, true), value: HotWaterPaymentKind.IncludedInTheRent },
    {
      name: HotWaterPaymentKindToString(HotWaterPaymentKind.IndividualMeasurement, true),
      value: HotWaterPaymentKind.IndividualMeasurement,
    },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.AccordingToTemplate, true), value: HotWaterPaymentKind.AccordingToTemplate },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.CostIsAdded, true), value: HotWaterPaymentKind.CostIsAdded },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.SeparateSubscription, true), value: HotWaterPaymentKind.SeparateSubscription },
  ];
  householdElectricityPaymentsBrf = [
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IncludedInTheRent, true),
      value: HouseholdElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IndividualMeasurement, true),
      value: HouseholdElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.SeparateSubscription, true),
      value: HouseholdElectricityPaymentKind.SeparateSubscription,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.AccordingToTemplate, true),
      value: HouseholdElectricityPaymentKind.AccordingToTemplate,
    },
  ];
  parkingElectricityPaymentsBrf = [
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IncludedInTheRent, true),
      value: ParkingElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IndividualMeasurement, true),
      value: ParkingElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption, true),
      value: ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.SpecifiedInSpecificTerms, true),
      value: ParkingElectricityPaymentKind.SpecifiedInSpecificTerms,
    },
  ];

  private _fb: FormBuilder;
  private _properties: Property[];
  protected get fb(): FormBuilder {
    return this._fb;
  }
  companies: any[] = [];
  readonly leaseRowStart: LocalDate;
  properties: any[];
  rentalObjectTypes: any[];
  logColumnDefinition: ColumnDefinition[];
  protected dimensionLabels: DimensionLabels = defaultDimensionLabels;

  private _filterPropertiesByCompany(company: string) {
    if (!this._properties) return;
    this.properties = this._properties
      .filter(p => p.company === company)
      .map(p => {
        return { name: p.toString(), value: p.id };
      });
    if (this.properties.length == 1 && this.isNew) {
      this.form.controls[rentalObjectFormModelNames.property].setValue(this.properties[0].value);
    }
  }

  private _getLeaseRowStart(): LocalDate {
    let date = new Date();
    date.setMonth(date.getMonth());
    date.setDate(1);
    return LocalDate.fromDate(date);
  }

  get showTemplateRows(): boolean {
    return this.currencyCode != null;
  }

  invoicePeriods = [
    { name: InvoicePeriodToString(InvoicePeriod.Month), value: InvoicePeriod.Month },
    { name: InvoicePeriodToString(InvoicePeriod.TwoMonths), value: InvoicePeriod.TwoMonths },
    { name: InvoicePeriodToString(InvoicePeriod.Quarter), value: InvoicePeriod.Quarter },
    { name: InvoicePeriodToString(InvoicePeriod.Tertrial), value: InvoicePeriod.Tertrial },
    { name: InvoicePeriodToString(InvoicePeriod.HalfYear), value: InvoicePeriod.HalfYear },
    { name: InvoicePeriodToString(InvoicePeriod.Year), value: InvoicePeriod.Year },
  ];

  protected getFormGroup(): FormGroup {
    let template = this.dataContext.leaseAgreementTemplate ? this.dataContext.leaseAgreementTemplate : new LeaseAgreementTemplate();
    this.templateForm = this.fb.group({
      invoicePeriodMonths: this.fc.createMandatory(
        'RENTAL_OBJECT_FORM.LEASE_AGREEMENT_TEMPLATE.INVOICE_PERIOD',
        template.invoicePeriodMonths
      ),
      noticePeriodLandlord: this.fc.createMandatory(
        'RENTAL_OBJECT_FORM.LEASE_AGREEMENT_TEMPLATE.NOTICE_PERIOD_LANDLORD',
        template.noticePeriodLandlord
      ),
      noticePeriodTenant: this.fc.createMandatory(
        'RENTAL_OBJECT_FORM.LEASE_AGREEMENT_TEMPLATE.NOTICE_PERIOD_TENANT',
        template.noticePeriodTenant
      ),
      specialTerms: this.fc.create('LEASE_AGREEMENT.SPECIAL_TERMS', template.specialTerms),
      heatingPayment: this.fc.createMandatory('LEASE_AGREEMENT.HEATING_PAYMENT', template.heatingPayment),
      hotWaterPayment: this.fc.createMandatory('LEASE_AGREEMENT.HOT_WATER_PAYMENT', template.hotWaterPayment),
      coldWaterPayment: this.fc.createMandatory('LEASE_AGREEMENT.COLD_WATER_PAYMENT', template.coldWaterPayment),
      householdElectricityPayment: this.fc.createMandatory(
        'LEASE_AGREEMENT.HOUSEHOLD_ELECTRICITY_PAYMENT',
        template.householdElectricityPayment
      ),
      parkingElectricityPayment: this.fc.create('LEASE_AGREEMENT.PARKING_ELECTRICITY_PAYMENT', template.parkingElectricityPayment),
      propertyTaxPaidBy: this.fc.create('PROPERTY_TAX.PAID_BY', template.propertyTaxPaidBy),
      propertyTaxCalculationBasis: this.fc.create('PROPERTY_TAX.CALCULATION_BASIS', template.propertyTaxCalculationBasis),
      propertyTaxPercentage: this.fc.create(
        'PROPERTY_TAX.PERCENTAGE_TEXT',
        toPropertyTaxPercentageDisplayValue(template.propertyTaxPercentage)
      ),
      propertyTaxCalculationMethod: this.fc.create('PROPERTY_TAX.CALCULATION_METHOD', template.propertyTaxCalculationMethod),
      propertyTaxArticlesNonRetroactive: this.fc.create('PROPERTY_TAX.NON_RETROACTIVE_ARTICLE', template.propertyTaxArticleId),
      propertyTaxArticlesRetroactive: this.fc.create('PROPERTY_TAX.RETROACTIVE_ARTICLE', template.propertyTaxRetroArticleId),
    });
    this.populateThePropertyTaxArticles(template.propertyTaxArticles);
    return this.fb.group({
      company: this.fc.createMandatory('RENTAL_OBJECT_FORM.COMPANY_LABEL', this.dataContext.companyId),
      property: this.fc.createMandatory('RENTAL_OBJECT_FORM.PROPERTY_LABEL', this.dataContext.propertyId),
      rentalId: this.fc.createWithOptions('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', this.dataContext.rentalId, {
        required: true,
        length: 50,
      }),
      note: this.fc.create('COMMON.NOTE', this.dataContext.note, Validators.maxLength(3000)),
      rentalObjectType: this.fc.createMandatory('RENTAL_OBJECT_FORM.RENTAL_OBJECT_TYPE_LABEL', this.dataContext.rentalObjectTypeId),
      rentalBlocks: this.fb.array((this.dataContext.rentalBlocks || []).map(block => this.createRentalBlockForm(block))),
      address: this.fb.group({}),
      rentalObjectDescription: this.fc.create(
        'RENTAL_OBJECT_FORM.OBJECT_DESCRIPTION',
        this.dataContext.rentalObjectDescription,
        null,
        null,
        'RENTAL_OBJECT_FORM.HELP_TEXT.OBJECT_DESCRIPTION'
      ),
      leaseAgreementTemplate: this.templateForm,
      lettingFeatures: this.fb.array([]),
      keys: this.fb.array([]),
      brfInnerFundTransactions: this.fb.array([]),
      brfMortgagePrescriptions: this.fb.array([]),
    });
  }

  templateForm: FormGroup;

  protected prepareSave(): TModel {
    this.submitEvent.emit();
    const model = super.prepareSave();
    model.companyId = this.form.controls[rentalObjectFormModelNames.company].value;
    model.propertyId = this.form.controls[rentalObjectFormModelNames.property].value;
    model.rentalObjectTypeId = this.form.controls[rentalObjectFormModelNames.rentalObjectType].value;
    model.leaseAgreementRowTemplates = this.dataContext.leaseAgreementRowTemplates;
    model.improvements = this.dataContext.improvements;
    model.currencyCode = this.currencyCode;
    model.rentalBlocks = (this.form.controls['rentalBlocks'] as FormArray).controls.map(v => new RentalObjectRentalBlock(v.value));

    model.keys = (this.form.controls['keys'] as FormArray).controls.map(v => new RentalObjectKey(v.value));
    model.brfInnerFundTransactions = (this.form.controls['brfInnerFundTransactions'] as FormArray).controls.map(
      v => new BrfInnerFundTransaction(v.value)
    );
    model.brfMortgagePrescriptions = (this.form.controls['brfMortgagePrescriptions'] as FormArray).controls.map(
      v => new BrfMortgagePrescription(v.value)
    );
    return model;
  }

  currencyCode: string = null;
  cultureName: string = null;
  licenseLimitsMaxedOut: boolean = false;
  licenseLimitsExceeded: boolean = false;

  protected init(data: TModel) {
    this.currencyCode = data.currencyCode;
    data.leaseAgreementRowTemplates = data.leaseAgreementRowTemplates.map(row => Object.assign(new LeaseRow(), row));
    data.improvements = data.improvements.map(row => Object.assign(new RentalObjectImprovement(), row));
    super.init(data);

    this.AddSubscription(
      this.companyService.getList().subscribe(data => {
        this.companies = data.map((item: Company) => {
          return { name: item.toString(), value: item.id, currencyCode: item.currencyCode };
        });
        if (this.companies.length == 1 && this.isNew) {
          this.form.controls[rentalObjectFormModelNames.company].setValue(this.companies[0].value);
        }
        if (this.form.controls[rentalObjectFormModelNames.company].value) {
          var currentCompany = this.companies.find(c => c.value === this.form.controls[rentalObjectFormModelNames.company].value);
          this.currencyCode = currentCompany.currencyCode;
          this.cultureName = currentCompany.cultureCode;
          this.loadDimensionLabels(currentCompany.value);
          if (!this.isNew) {
            this.companies = this.companies.filter(c => c.currencyCode === currentCompany.currencyCode);
          }
        }
      })
    );

    if (this.isNew) {
      this.AddSubscription(
        this.licenseService
          .getLimitsComparison()
          .pipe(take(1))
          .subscribe(res => {
            this.licenseLimitsMaxedOut = this._isLicenseLimitMaxedOut(res);
            this.licenseLimitsExceeded = this._isLicenseLimitExceeded(res);
          })
      );
    }

    this.AddSubscription(
      this.propertyService.getList().subscribe(data => {
        this._properties = data;
        if (!this.properties) {
          let company = this.form.controls[rentalObjectFormModelNames.company].value;
          if (company) {
            this._filterPropertiesByCompany(company);
          }
        }
      })
    );

    this.AddSubscription(
      this.form.controls[rentalObjectFormModelNames.company].valueChanges.subscribe(value => {
        this._filterPropertiesByCompany(value);
        if (value) {
          let company = this.companies.find(c => c.value === value);
          if (company) {
            this.currencyCode = company.currencyCode;
            this.cultureName = company.cultureName;
          }
        }
        this.loadDimensionLabels(value);
      })
    );

    this.AddSubscription(
      this.rentalObjectTypeService.getByCategory(this.createModel().category).subscribe(data => {
        this.rentalObjectTypes = data.map((item: RentalObjectType) => {
          return {
            name: item.name,
            value: item.id,
            numberOfRooms: item.numberOfRooms,
            isGarage: item.isGarage,
            parkingHasElectricity: item.parkingHasElectricity,
            parkingHasRoof: item.parkingHasRoof,
          };
        });
      })
    );
  }

  private _isLicenseLimitMaxedOut(limitsComparison: LicenceLimitComparison): boolean {
    switch (this.category) {
      case RentalObjectCategory.Dwelling:
        return this._isAtOrAboveLimit(limitsComparison.dwellingLevel, limitsComparison.dwellingLimit);
      case RentalObjectCategory.Parking:
        return this._isAtOrAboveLimit(limitsComparison.parkingLevel, limitsComparison.parkingLimit);
      case RentalObjectCategory.Other:
        return this._isAtOrAboveLimit(limitsComparison.otherObjectLevel, limitsComparison.otherObjectLimit);
      case RentalObjectCategory.Premises:
        return this._isAtOrAboveLimit(limitsComparison.premisesSqmLevel, limitsComparison.premisesSqmLimit);
      default:
        return false;
    }
  }

  private _isLicenseLimitExceeded(limitsComparison: LicenceLimitComparison): boolean {
    switch (this.category) {
      case RentalObjectCategory.Dwelling:
        return this._isAboveLimit(limitsComparison.dwellingLevel, limitsComparison.dwellingLimit);
      case RentalObjectCategory.Parking:
        return this._isAboveLimit(limitsComparison.parkingLevel, limitsComparison.parkingLimit);
      case RentalObjectCategory.Other:
        return this._isAboveLimit(limitsComparison.otherObjectLevel, limitsComparison.otherObjectLimit);
      case RentalObjectCategory.Premises:
        return this._isAboveLimit(limitsComparison.premisesSqmLevel, limitsComparison.premisesSqmLimit);
      default:
        return false;
    }
  }

  private _isAboveLimit(level?: number, limit?: number): boolean {
    return typeof level === 'number' && typeof limit === 'number' && level > limit;
  }

  private _isAtOrAboveLimit(level?: number, limit?: number): boolean {
    return typeof level === 'number' && typeof limit === 'number' && level >= limit;
  }

  faultNotificationsLoaded: boolean;

  setFaultNotificationsVisibility(show: boolean) {
    if (this.faultNotificationsLoaded) {
      return;
    }
    this.AddSubscription(
      this.faultNotificationService.getByRentalObject(this.dataContext.id).subscribe(f => {
        this.dataContext.faultNotifications = f;
        this.faultNotificationsLoaded = true;
      })
    );
  }

  onFaultNotificationRowClicked(e: { context: FaultNotification; event: any }): void {
    this.navigation.navigate(['fault-notification', 'notifications', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  protected setAddress(address: Address) {
    if (!address) return;
    let adr = this.form.controls['address'] as FormGroup;
    adr.controls['city'].setValue(address.city);
    adr.controls['postalCode'].setValue(address.postalCode);
    adr.controls['street'].setValue(address.street);
  }

  protected override initializeSection(section: string) {
    switch (section) {
      case 'logs':
        this.AddSubscription(
          this.logService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
            this.logs = res.items;
          })
        );
    }
    super.initializeSection(section);
  }

  logs: LogRecord[] = [];

  get rentalBlockForms(): FormArray {
    return this.form.controls['rentalBlocks'] as FormArray;
  }

  addRentalBlock() {
    this.rentalBlockForms.push(this.createRentalBlockForm(new RentalObjectRentalBlock()));
  }

  removeRentalBlock(index: number) {
    this.rentalBlockForms.removeAt(index);
    this.form.markAsDirty();
  }

  onLeaseRowsChange(newRows: LeaseRow[]) {}

  onItemSourceChange(newRows: LeaseRow[]) {
    this.dataContext.leaseAgreementRowTemplates = newRows;
  }

  protected executedWithResult(result: boolean, command: CommandBase): void {
    super.executedWithResult(result, command);
    if (!result) {
      return;
    }
    if (command.id === rentalObjectCommand.linkDocument && this.rentalObjectDocumentManager) {
      this.rentalObjectDocumentManager.refresh();
    }
  }

  private createRentalBlockForm(block: RentalObjectRentalBlock): FormGroup {
    const form = this.fb.group(
      {
        id: [block?.id ?? null],
        fromDate: this.fc.create('RENTAL_OBJECT.BLOCKED_FROM', block?.fromDate),
        toDate: this.fc.create('RENTAL_OBJECT.BLOCKED_TO', block?.toDate),
        reason: this.fc.create('RENTAL_OBJECT.BLOCKING_REASON', block?.reason),
        project: this.fc.createMaxLength(this.dimensionLabels.projectLabel, block?.project, 10),
      },
      { validators: this._rentalBlockDateValidator() }
    );

    return form;
  }

  private _rentalBlockDateValidator() {
    return (form: AbstractControl): ValidationErrors | null => {
      if (!(form instanceof FormGroup)) {
        return null;
      }

      const fromControl = form.get('fromDate');
      const toControl = form.get('toDate');

      const fromDate = this._toComparableDate(fromControl?.value);
      const toDate = this._toComparableDate(toControl?.value);

      if (!fromDate && !toDate) {
        return {
          fromDate: {
            message: 'RENTAL_OBJECT.VALIDATION.BLOCK_PERIOD_DATE_REQUIRED',
            params: {},
          },
          toDate: {
            message: 'RENTAL_OBJECT.VALIDATION.BLOCK_PERIOD_DATE_REQUIRED',
            params: {},
          },
        };
      }

      if (fromDate && toDate && toDate.getTime() < fromDate.getTime()) {
        return {
          fromDate: {
            message: 'RENTAL_OBJECT.VALIDATION.BLOCK_PERIOD_TO_BEFORE_FROM',
            params: {},
          },
          toDate: {
            message: 'RENTAL_OBJECT.VALIDATION.BLOCK_PERIOD_TO_BEFORE_FROM',
            params: {},
          },
        };
      }

      return null;
    };
  }

  private _toComparableDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value.toDate === 'function') {
      const dateValue = value.toDate();
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }
    }

    if (value.year && value.month && value.day) {
      const dateValue = new Date(value.year, value.month - 1, value.day);
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const dateValue = new Date(value);
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    return null;
  }

  private loadDimensionLabels(companyId: string) {
    this.AddSubscription(
      this.dimensionLabelsService.getForCompany(companyId).subscribe(labels => {
        this.dimensionLabels = labels;
        this.rentalBlockForms?.controls.forEach(control => {
          this.dimensionLabelsService.setControlCaption(control.get('project'), labels.projectLabel);
        });
      })
    );
  }

  propertyTaxArticlesNonRetroactive: SelectionItem[] = [];
  propertyTaxArticlesRetroactive: SelectionItem[] = [];

  private populateThePropertyTaxArticles(propertyTaxArticles: Article[]): void {
    const articles = (propertyTaxArticles ?? []).map(a => (a instanceof Article ? a : new Article(a)));

    this.propertyTaxArticlesNonRetroactive = articles
      .filter(x => !x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    this.propertyTaxArticlesRetroactive = articles
      .filter(x => x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    // Keep form controls in sync without emitting change events.
    const nonRetroId = this.dataContext?.leaseAgreementTemplate?.propertyTaxArticleId ?? null;
    const retroId = this.dataContext?.leaseAgreementTemplate?.propertyTaxRetroArticleId ?? null;

    // Only set ids that exist in the current option set to avoid invalid values.
    const hasNonRetro = this.propertyTaxArticlesNonRetroactive.some(x => x.value === nonRetroId);
    const hasRetro = this.propertyTaxArticlesRetroactive.some(x => x.value === retroId);

    this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.setValue(hasNonRetro ? nonRetroId : null, { emitEvent: false });
    this.templateForm?.get('propertyTaxArticlesRetroactive')?.setValue(hasRetro ? retroId : null, { emitEvent: false });
  }
}
